<script lang="ts" setup>
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useDisplay } from 'vuetify';
import useRenditionStore from '../../stores/rendition';
import useNavigationStore from '../../stores/navigation';
import useUiStore from '../../stores/ui';
import useNavigationTriggerStore from '../../stores/navigation-trigger';
import type { EnhancedPage } from '../../types/navigation';
import type { GoToItem } from '../../types';
import useLocale from '../../composables/useLocale';
import { isPageActive } from '../../util/page-range-calculators';

type Emits = { (e: 'goToContent', item: GoToItem): void };
const emit = defineEmits<Emits>();

const { t } = useLocale();
const { setNavigationTrigger } = useNavigationTriggerStore();

type Props = {
  item: EnhancedPage;
};
const props = defineProps<Props>();

const { pages, currentPage, currentPageIndex } = storeToRefs(useNavigationStore());
const { isItemShownOnPage, isSpread } = storeToRefs(useRenditionStore());
const { toggleTableOfContentDrawer } = useUiStore();
const { smAndDown } = useDisplay();

const isActive = computed(() =>
  isPageActive({
    itemPage: props.item.page,
    currentPage: currentPage.value?.page ?? null,
    isSpread: isSpread.value,
    pages: pages.value,
    currentPageIndex: currentPageIndex.value,
    isItemShownOnPage: isItemShownOnPage.value,
  }),
);

const goToPageFromItem = (page: EnhancedPage) => {
  if (smAndDown.value) {
    toggleTableOfContentDrawer(false);
  }

  const currentPage = pages.value.find((p) => isItemShownOnPage.value(p));

  setNavigationTrigger('PAGE_LIST');
  emit('goToContent', {
    target: page.cfi || page.href,
    isGoingForwardsInBook: currentPage ? page.page > currentPage.page : true,
  });
};
</script>

<template>
  <div class="item">
    <v-list-item
      class="text-left px-2"
      link
      color="primary"
      :active="isActive"
      :data-testid="`bp-page-item-${item.page}`"
      @click.stop="goToPageFromItem(item)"
    >
      <v-list-item-title class="label"> {{ t('page_navigation_item') }} {{ item.page }} </v-list-item-title>
    </v-list-item>
  </div>
</template>

<style scoped lang="scss">
.item .v-list-item {
  min-height: unset;
}
</style>
